import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { useTheme } from 'styled-components';
import { Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { SKILL_CATEGORY_COLORS } from '../StudentsProgress.mock';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

// Predefined vibrant colors for orbs
const ORB_COLORS = [
  '#FF6384', // pink
  '#36A2EB', // blue
  '#FFCE56', // yellow
  '#4BC0C0', // teal
  '#9966FF', // purple
  '#FF9F40', // orange
  '#1BD171', // green
  '#E74C3C', // red
  '#3498DB', // light blue
  '#9B59B6', // violet
  '#1ABC9C', // turquoise
  '#F39C12', // amber
];

// Generate a consistent random color based on index
const getOrbColor = (index) => {
  return ORB_COLORS[index % ORB_COLORS.length];
};

export const SkillsBarChart = ({ data, size = 'large' }) => {
  const theme = useTheme();
  const primaryColor = theme.config.colors.primary;

  // Memoize bar colors - compute even if data is empty to satisfy Rules of Hooks
  const barColors = useMemo(() => {
    if (!data || data.length === 0) return [];
    return data.map(
      (item) => SKILL_CATEGORY_COLORS[item.category] || primaryColor
    );
  }, [data, primaryColor]);

  // Memoize orb colors - compute even if data is empty to satisfy Rules of Hooks
  const orbColors = useMemo(() => {
    if (!data || data.length === 0) return [];
    return data.map((_, index) => getOrbColor(index));
  }, [data]);

  // Plugin to draw colored orbs next to Y-axis labels
  const yAxisOrbPlugin = useMemo(
    () => ({
      id: 'yAxisOrb',
      afterDraw: (chart) => {
        const {
          ctx,
          scales: { y },
        } = chart;

        y.ticks.forEach((_, index) => {
          const yPos = y.getPixelForTick(index);
          const xPos = y.left - 12;

          ctx.beginPath();
          ctx.arc(xPos, yPos, 4, 0, Math.PI * 2);
          ctx.fillStyle = orbColors[index] || primaryColor;
          ctx.fill();
        });
      },
    }),
    [orbColors, primaryColor]
  );

  // Early return AFTER all hooks have been called
  if (!data || data.length === 0) {
    return null;
  }

  const chartData = {
    labels: data.map((item) => item.label),
    datasets: [
      {
        data: data.map((item) => item.percentage),
        backgroundColor: barColors,
        borderRadius: 4,
        barThickness: 12,
      },
    ],
  };

  const options = {
    indexAxis: 'y',
    responsive: true,
    maintainAspectRatio: false,
    layout: {
      padding: {
        left: 20,
      },
    },
    plugins: {
      legend: {
        display: false,
      },
      datalabels: {
        display: false,
      },
      tooltip: {
        callbacks: {
          label: (context) => `${context.raw}%`,
        },
        backgroundColor: theme.config.colors.secondary,
        titleColor: theme.config.colors.light,
        bodyColor: theme.config.colors.light,
        padding: 12,
        cornerRadius: 8,
      },
    },
    scales: {
      x: {
        min: 0,
        max: 100,
        border: {
          display: true,
          color: theme.config.colors.gray_100,
        },
        grid: {
          color: theme.config.colors.gray_100,
          drawBorder: true,
        },
        ticks: {
          color: theme.config.colors.gray_400,
          font: {
            size: 11,
          },
          callback: (value) => `${value}%`,
          stepSize: 20,
        },
      },
      y: {
        border: {
          display: true,
          color: theme.config.colors.gray_100,
        },
        grid: {
          display: true,
          color: theme.config.colors.gray_100,
          drawBorder: true,
        },
        ticks: {
          color: theme.config.colors.gray_400,
          font: {
            size: 11,
            weight: 'bold',
          },
          padding: 20,
          crossAlign: 'far',
        },
      },
    },
  };

  return (
    <div
      style={{ height: size === 'small' ? '18rem' : '24rem', width: '100%' }}
    >
      <Bar data={chartData} options={options} plugins={[yAxisOrbPlugin]} />
    </div>
  );
};

SkillsBarChart.propTypes = {
  data: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      percentage: PropTypes.number.isRequired,
      category: PropTypes.string.isRequired,
    })
  ),
  size: PropTypes.oneOf(['small', 'large']),
};
